import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Clock, CheckCircle, XCircle,
  CreditCard, TrendingUp, ArrowRight, Mail, BarChart3
} from 'lucide-react';
import api from '../../utils/api';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/admin/reports').then((res) => {
      setStats(res.data.stats);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  const statCards = [
    {
      label: 'Total Registrations',
      value: stats?.totalRegistrations ?? 0,
      icon: Users,
      bg: '#dbeafe', color: '#1e40af'
    },
    {
      label: 'Pending Referral',
      value: stats?.pendingReferral ?? 0,
      icon: Clock,
      bg: '#fef3c7', color: '#92400e'
    },
    {
      label: 'Referral Approved',
      value: stats?.referralApproved ?? 0,
      icon: CheckCircle,
      bg: '#e0e7ff', color: '#3730a3'
    },
    {
      label: 'Payment Received',
      value: stats?.paymentReceived ?? 0,
      icon: CreditCard,
      bg: '#ede9fe', color: '#5b21b6'
    },
    {
      label: 'Active Members',
      value: stats?.activeMembers ?? 0,
      icon: TrendingUp,
      bg: '#dcfce7', color: '#14532d'
    },
    {
      label: 'Rejected',
      value: stats?.rejected ?? 0,
      icon: XCircle,
      bg: '#fee2e2', color: '#7f1d1d'
    },
  ];

  // Quick Actions — Bug fix: View All Members moved here
  const quickActions = [
    {
      label: 'View All Members',
      desc: 'Browse and manage all member registrations',
      icon: Users,
      color: 'var(--primary)',
      bg: '#dbeafe',
      onClick: () => navigate('/admin/members'),
    },
    {
      label: 'Membership Report',
      desc: 'Generate and export membership reports',
      icon: BarChart3,
      color: '#16a34a',
      bg: '#dcfce7',
      onClick: () => navigate('/admin/reports'),
    },
    {
      label: 'Email Settings',
      desc: 'Configure Gmail for member notifications',
      icon: Mail,
      color: '#d97706',
      bg: '#fef3c7',
      onClick: () => navigate('/admin/email-config'),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h2>Admin Dashboard</h2>
        <p>Bombay Psychiatric Society — Membership Overview</p>
      </div>

      {/* Stat Cards */}
      <div className="grid-3" style={{ marginBottom: 24 }}>
        {statCards.map((card) => (
          <div key={card.label} className="stat-card"
            style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
            <div style={{ background: card.bg, borderRadius: 12, padding: 14, flexShrink: 0 }}>
              <card.icon size={24} style={{ color: card.color }} />
            </div>
            <div>
              <div className="stat-value" style={{ fontSize: 30, color: card.color }}>
                {card.value}
              </div>
              <div className="stat-label">{card.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Total Payment Collected — Bug fix: Removed $ icon, use ₹ symbol only */}
      <div className="card" style={{ padding: 28, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{
              fontSize: 13, color: 'var(--text-muted)',
              fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '0.05em', marginBottom: 8
            }}>
              Total Payment Collected
            </div>
            <div style={{
              fontSize: 42, fontWeight: 700,
              color: 'var(--primary)',
              fontFamily: "'Playfair Display', serif"
            }}>
              {/* Bug fix: Removed $ icon — showing ₹ only */}
              ₹ {Number(stats?.totalPaymentAmount || 0).toLocaleString('en-IN', {
                minimumFractionDigits: 2
              })}
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 6 }}>
              Total offline payments recorded in the system
            </div>
          </div>

        </div>
      </div>

      {/* Quick Actions — Bug fix: View All Members button moved here */}
      <div className="card" style={{ padding: 28 }}>
        <h3 style={{ fontSize: 16, color: 'var(--primary)', marginBottom: 20 }}>
          Quick Actions
        </h3>
        <div className="grid-3">
          {quickActions.map((action) => (
            <div
              key={action.label}
              onClick={action.onClick}
              style={{
                background: 'var(--bg)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)',
                padding: '20px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = action.color;
                e.currentTarget.style.background = action.bg;
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = 'var(--shadow-md)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.background = 'var(--bg)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{
                width: 42, height: 42, borderRadius: 10,
                background: action.bg, border: `1px solid ${action.color}30`,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <action.icon size={20} style={{ color: action.color }} />
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>
                  {action.label}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
                  {action.desc}
                </div>
              </div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 4,
                fontSize: 13, color: action.color, fontWeight: 500, marginTop: 'auto'
              }}>
                Open <ArrowRight size={13} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
